<template>
  <CModal :show.sync="show" centered :close-on-backdrop="false" class="modal-cropper">
    <template #header>
      <div>
        <div class="d-flex">
          <CIcon name="cipUpload2" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div class="align-self-center">
            <h5 class="modal-title">{{ settings.modal.title }}</h5>
            <p class="m-0">{{ settings.modal.subtitle }}</p>
          </div>
        </div>
      </div>
      <CButtonClose @click="dismiss"/>
    </template>

    <PComponentLoader :loading="loading">
      <div class="cropper-wrap" :class="wrapperClass">
        <div class="cropper-container" :style="containerStyle">
          <cropper ref="cropper"
                   :src="file_url"
                   :min-height="settings.minHeight"
                   :min-width="settings.minWidth"
                   :resize-image="resizeImage"
                   :background-class="backgroundClass"
                   :foreground-class="foregroundClass"
                   :stencil-props="stencilProps"
                   :stencil-size="stencilSize"
                   :default-size="defaultSize"
                   :canvas="canvas"
                   :debounce="settings.debounce"
                   :transitions="settings.transitions"
                   :move-image="settings.moveImage"
                   :image-restriction="settings.imageRestriction"
                   :stencil-component="settings.stencilComponent"
                   @ready="onReady"
                   @change="onChange"
                   class="cropper"
          />
        </div>
      </div>

      <CRow class="mt-3" v-if="settings.imageRestriction === 'none'">
        <CCol col="5">
          <label class="font-weight-medium" v-translate translate-context="event_wizard.cropper">Position</label>
          <div class="btn-group-squared btn-group-squared__logo_position btn-group-toggle" style="max-width: 180px">
            <CButton @click="align(position.value)" class="mr-2 mb-2" color="secondary"
                     :class="{'active': selectedLogoPosition === position.value}"
                     v-for="(position, index) in logoPositions" :key="index" :style="position.style">
              <CIcon size="xl" :name="position.iconName" class="m-0" :style="position.iconStyle"/>
            </CButton>
          </div>
        </CCol>
      </CRow>
      <CRow class="mt-3" v-else>
        <CCol>
          <label class="font-weight-medium" v-translate translate-context="event_wizard.cropper">Zoom</label>
          <PCropperNavigation
              :zoom="zoom" @change="onZoom"
              style="margin-top: 12px"/>
        </CCol>
        <CCol>
          <label class="font-weight-medium" v-translate translate-context="event_wizard.cropper">Background</label>
          <PColorPicker native disable-alpha :color="colorBg" @change="color => colorBg = color.hex"/>
        </CCol>
      </CRow>
    </PComponentLoader>

    <template #footer>
      <div class="d-flex w-100">
        <div class="flex-grow-1 mr-1">
          <CButton block @click="dismiss"
                   color="secondary"
                   v-translate translate-context="event_wizard.cropper">Close
          </CButton>
        </div>
        <div class="flex-grow-1 text-center ml-1">
          <CButton block
                   @click="onSet"
                   :disabled="!fileEdited"
                   color="primary">
            <span v-translate translate-context="event_wizard.cropper">Set</span>
          </CButton>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>

import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import PCropperNavigation from "@/domain/core/components/upload/PCropperNavigation.vue";
import PColorPicker from "@/domain/core/components/PColorPicker.vue";
import {cropperTypes, logoPositions} from "@/domain/core/constant/cropper";

export default {
  name: "PCropper",
  components: {PColorPicker, PCropperNavigation, PComponentLoader},
  emits: ['file-edited', 'dismiss'],
  data() {
    return {
      show: false,
      loading: true,
      fileEdited: null,
      zoom: 0,
      colorBg: null,
      fillColor: 'transparent',
      selectedLogoPosition: null,
      loadedFile: null,
      originalSize: null,
    }
  },
  props: {
    cropperType: {
      type: String,
      required: false,
      default: 'circle'
    },
    file: {
      type: [File],
      required: true
    }
  },
  watch: {
    colorBg: {
      handler() {
        this.fillColor = this.colorBg
        this.changeBgColor(this.fillColor);
        this.refresh()
      }
    }
  },
  computed: {
    containerStyle() {
      return {
        aspectRatio: this.settings.aspectRatio,
        height: cropperTypes.barLandscape !== this.cropperType ? '420px' : 'auto'
      }
    },
    logoPositions() {
      return logoPositions.filter(position => position.value === 'top-center' || position.value === 'bottom-center' || position.value === 'center')
    },
    settings() {
      const settings = {
        [cropperTypes.circle]: {
          modal: {
            title: this.$pgettext('event_wizard.cropper.title', 'Edit image'),
            subtitle: this.$pgettext('event_wizard.cropper.subtitle', 'For best results, use a .PNG or .JPG of 300x300 px'),
          },
          imageRestriction: 'stencil',
          debounce: false,
          transitions: false,
          aspectRatio: 1,
          minWidth: 100,
          minHeight: 100,
          canvas: {
            width: 300,
            height: 300,
          },
          moveImage: {
            touch: true,
            mouse: true,
          },
          stencilComponent: 'rectangle-stencil'
        },
        [cropperTypes.barLandscape]: {
          modal: {
            title: this.$pgettext('event_wizard.cropper.title', 'Edit image'),
            subtitle: this.$pgettext('event_wizard.cropper.subtitle', 'For best results, use a 1620 px width .PNG or .JPG'),
          },
          bgClass: 'bg-landscape',
          fgClass: 'fg-transparent',
          debounce: false,
          transitions: true,
          imageRestriction: 'none',
          aspectRatio: 3 / 2,
          minWidth: 300,
          minHeight: 300,
          canvas: {
            width: 1620,
            height: 1080,
          },
          resizeImage: {
            touch: false,
            wheel: false,
            adjustStencil: true
          },
          moveImage: false,
          stencilComponent: 'rectangle-stencil'
        },
        [cropperTypes.barPortrait]: {
          modal: {
            title: this.$pgettext('event_wizard.cropper.title', 'Edit image'),
            subtitle: this.$pgettext('event_wizard.cropper.subtitle', 'For best results, use a 1080 px width .PNG or .JPG'),
          },
          bgClass: 'bg-portrait',
          fgClass: 'fg-transparent',
          debounce: false,
          transitions: true,
          imageRestriction: 'none',
          aspectRatio: 2 / 3,
          minWidth: 300,
          minHeight: 300,
          canvas: {
            width: 1080,
            height: 1620
          },
          resizeImage: {
            touch: false,
            wheel: false,
            adjustStencil: true
          },
          moveImage: false,
          stencilComponent: 'rectangle-stencil'
        },
      }
      return settings[this.cropperType]
    },

    file_url() {
      if (!this.file) return null
      return URL.createObjectURL(this.file)
    },
    canvas() {
      return {
        ...this.settings.canvas,
        fillColor: this.fillColor
      }
    },
    resizeImage() {
      return this.settings.resizeImage || {
        touch: true,
        wheel: {
          ratio: 0.03
        },
        adjustStencil: false
      }
    },
    stencilProps() {
      return {
        handlers: {},
        lines: {
          north: false,
          west: false,
          south: false,
          east: false,
        },
        linesClasses: {
          default: 'line-stencil',
        },
        movable: false,
        resizable: false,
        scalable: false,
        aspectRatio: this.settings.aspectRatio

      }
    },
    wrapperClass() {
      return {
        'no-drag': !this.settings.moveImage,
        'circle-mask': this.cropperType === cropperTypes.circle
      }
    },
    backgroundClass() {
      let classes = ['bg-cropper']
      if (this.settings.bgClass) classes.push(this.settings.bgClass)
      if (!this.colorBg && this.cropperType === cropperTypes.circle) classes.push('bg-transparent')
      //if (this.colorBg) classes.push('bg-color')
      return classes.join(' ')
    },
    foregroundClass() {
      let classes = []
      if (this.settings.fgClass) classes.push(this.settings.fgClass)
      //if (this.colorBg) classes.push('fg-color');
      return classes.join(' ');
    }
  },
  methods: {
    applyZoom(value) {
      const cropper = this.$refs.cropper
      if (cropper) {
        cropper.zoom(value)
        if (value > 1) {
          this.zoom += value
        } else {
          this.zoom -= value
        }
      }
    },
    align(position) {
      const cropper = this.$refs.cropper
      const {
        coordinates, image, visibleArea, canvas
      } = cropper.getResult()
      this.selectedLogoPosition = position

      if (!this.originalSize) this.originalSize = coordinates
      if (cropper) {
        if (position === 'top-left') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: 0,
            top: 0,
          })
        } else if (position === 'top-right') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: -(cropper.coordinates.width - image.width),
            top: 0,
          })
        } else if (position === 'top-center') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: -(cropper.coordinates.width - image.width) / 2,
            top: 0,
          })
        } else if (position === 'bottom-left') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: 0,
            top: -(cropper.coordinates.height - image.height),
          })
        } else if (position === 'bottom-right') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: -(cropper.coordinates.width - image.width),
            top: -(cropper.coordinates.height - image.height),
          })
        } else if (position === 'bottom-center') {
          cropper.setCoordinates({
            width: cropper.coordinates.width,
            height: cropper.coordinates.height,
            left: -(cropper.coordinates.width - image.width) / 2,
            top: -(cropper.coordinates.height - image.height),
          })
        }
      }
    },
    changeBgColor(color) {
      this.$el.querySelector('.bg-cropper').style.backgroundColor = color
      this.$el.querySelector('.bg-cropper').style.backgroundImage = 'none'
      //this.$el.querySelector('.fg-cropper').style.backgroundColor = 'transparent'
    },
    refresh() {
      this.$refs.cropper.refresh()
    },
    async open() {
      this.show = true
      await this.$nextTick()
      this.loading = false

    },
    dismiss() {
      this.colorBg = null
      this.$emit('dismiss', this.fileEdited !== null)
      this.show = false
    },
    defaultSize({imageSize, visibleArea}) {
      return {
        width: (visibleArea || imageSize).width,
        height: (visibleArea || imageSize).height,
      };
    },
    stencilSize({boundaries}) {
      return {
        width: boundaries.width,
        height: boundaries.height
      }
    },
    async onSet() {
      await this.$emit('file-edited', this.fileEdited)
      this.show = false
    },
    updateZoom() {
      const cropper = this.$refs.cropper;
      if (cropper) {
        const {coordinates, imageSize} = cropper
        if (imageSize.width / imageSize.height > coordinates.width / coordinates.height) {
          // Determine the position of slider bullet
          // It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
          this.zoom =
              (cropper.imageSize.height - cropper.coordinates.height) /
              (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
        } else {
          this.zoom =
              (cropper.imageSize.width - cropper.coordinates.width) /
              (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
        }
      }
    },
    async onReady() {
      // default bar placement
      if (this.cropperType === cropperTypes.barPortrait || this.cropperType === cropperTypes.barLandscape)
        this.align(this.logoPositions[1].value)

      if (this.cropperType === cropperTypes.circle)
        this.$refs.cropper.zoom(1.3);
    },
    onChange({coordinates, canvas,}) {
      this.updateZoom()

      const fileName = [...this.file.name.split('.').slice(0, -1), 'png'].join('.');
      canvas.toBlob((blob) => {
        if (blob) {
          this.fileEdited = new File([blob], fileName, {
            type: this.file.type,
            lastModified: Date.now(),
          })
        }
      }, 'image/png', 1)
    },
    onZoom(value) {
      const cropper = this.$refs.cropper;
      if (cropper) {
        if (cropper.imageSize.height < cropper.imageSize.width) {
          const minHeight = cropper.sizeRestrictions.minHeight;
          const imageHeight = cropper.imageSize.height;
          // Determine the current absolute zoom and the new absolute zoom
          // to calculate the sought relative zoom value
          cropper.zoom(
              (imageHeight - this.zoom * (imageHeight - minHeight)) /
              (imageHeight - value * (imageHeight - minHeight))
          );
        } else {
          const minWidth = cropper.sizeRestrictions.minWidth;
          const imageWidth = cropper.imageSize.width;
          cropper.zoom(
              (imageWidth - this.zoom * (imageWidth - minWidth)) /
              (imageWidth - value * (imageWidth - minWidth))
          );
        }
      }
    },
  }
}
</script>